import React, {
  useEffect,
  useState
} from "react";

import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import { db } from "../../firebase";

export default function StockList({
  companyId: companyIdProp
}) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = localStorage.getItem("company_id") || user?.company_id || companyIdProp || "";

  const [
    stockItems,
    setStockItems
  ] = useState([]);

  const [
    filteredItems,
    setFilteredItems
  ] = useState([]);

  const [
    search,
    setSearch
  ] = useState("");

  const [
    categoryFilter,
    setCategoryFilter
  ] = useState("all");

  const [
    loading,
    setLoading
  ] = useState(true);

  /* =================================================
     FETCH STOCK
  ================================================= */

  const fetchStock =
    async () => {

      setLoading(true);

      try {

        if (!companyId) {

          throw new Error(
            "companyId required"
          );
        }

        // =========================================
        // SST SAFE CACHE FETCH
        // inventory_summary = cache only
        // NO MERGING
        // NO DUPLICATE AGGREGATION
        // =========================================

        const stockQuery =
          query(

            collection(
              db,
              "inventory_summary"
            ),

            where(
              "company_id",
              "==",
              companyId
            )
          );

        const querySnapshot =
          await getDocs(
            stockQuery
          );

        const stockData =
          querySnapshot.docs.map(
            (docItem) => {

              const d =
                docItem.data();

              return {

                id:
                  docItem.id,

                ...d,

                qty:
                  Number(
                    d.qty || 0
                  ),

                defective_stock:
                  Number(
                    d.defective_stock || 0
                  ),

                scrap_stock:
                  Number(
                    d.scrap_stock || 0
                  ),
              };
            }
          );

        // =========================================
        // NEGATIVE STOCK AUDIT
        // =========================================

        const negativeItems =
          stockData.filter(

            (item) =>

              Number(
                item.qty || 0
              ) < 0
          );

        if (
          negativeItems.length > 0
        ) {

          console.error(
            "NEGATIVE STOCK DETECTED",
            negativeItems
          );
        }

        // =========================================
        // SST SAFE
        // NO MANUAL MERGING
        // =========================================

        setStockItems(
          stockData
        );

        setFilteredItems(
          stockData
        );

      } catch (error) {

        console.error(
          "Stock Fetch Error:",
          error
        );

      } finally {

        setLoading(false);
      }
    };

  /* =================================================
     INITIAL LOAD
  ================================================= */

  useEffect(() => {

    if (companyId) fetchStock();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  /* =================================================
     FILTERS
  ================================================= */

  useEffect(() => {

    let updatedList =
      [...stockItems];

    // =========================================
    // SEARCH
    // =========================================

    if (search) {

      updatedList =
        updatedList.filter(

          (item) =>

            item.item_name
              ?.toLowerCase()
              .includes(
                search.toLowerCase()
              )

            ||

            item.item_code
              ?.toLowerCase()
              .includes(
                search.toLowerCase()
              )

            ||

            item.item_id
              ?.toLowerCase()
              .includes(
                search.toLowerCase()
              )
        );
    }

    // =========================================
    // CATEGORY
    // =========================================

    if (
      categoryFilter !== "all"
    ) {

      updatedList =
        updatedList.filter(

          (item) =>

            item.category
              ?.toLowerCase()

            ===

            categoryFilter
              .toLowerCase()
        );
    }

    setFilteredItems(
      updatedList
    );

  }, [
    search,
    categoryFilter,
    stockItems
  ]);

  /* =================================================
     CATEGORY LIST
  ================================================= */

  const categories = [

    "all",

    ...new Set(

      stockItems

        .map(
          (item) =>
            item.category
        )

        .filter(Boolean)
    ),
  ];

  /* =================================================
     UI
  ================================================= */

  return (

    <div className="container mt-4">

      <h2 className="mb-4">
        Stock List
      </h2>

      {/* ===================================== */}
      {/* FILTERS */}
      {/* ===================================== */}

      <div className="row mb-3">

        <div className="col-md-8">

          <input
            type="text"
            className="form-control"
            placeholder="
              Search by item name /
              item code
            "
            value={search}
            onChange={(e) =>

              setSearch(
                e.target.value
              )
            }
          />
        </div>

        <div className="col-md-4">

          <select
            className="form-control"
            value={categoryFilter}
            onChange={(e) =>

              setCategoryFilter(
                e.target.value
              )
            }
          >

            {categories.map(

              (cat, index) => (

                <option
                  key={index}
                  value={cat}
                >

                  {cat === "all"

                    ? "All Categories"

                    : cat}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {/* ===================================== */}
      {/* LOADING */}
      {/* ===================================== */}

      {loading ? (

        <p>
          Loading stock...
        </p>

      ) : (

        <div className="table-responsive">

          <table className="
            table
            table-bordered
            table-striped
          ">

            <thead className="table-dark">

              <tr>

                <th>
                  Item Code
                </th>

                <th>
                  Item Name
                </th>

                <th>
                  Category
                </th>

                <th>
                  Unit
                </th>

                <th>
                  Current Stock
                </th>

                <th>
                  Defective Stock
                </th>

                <th>
                  Scrap Stock
                </th>

                <th>
                  Last Updated
                </th>
              </tr>
            </thead>

            <tbody>

              {filteredItems.length > 0 ? (

                filteredItems.map(

                  (item) => (

                    <tr key={item.id}>

                      <td>

                        {item.item_code ||

                          item.item_id ||

                          "-"}
                      </td>

                      <td>
                        {item.item_name || "-"}
                      </td>

                      <td>
                        {item.category || "-"}
                      </td>

                      <td>
                        {item.unit || "N/A"}
                      </td>

                      <td className={

                        Number(
                          item.qty || 0
                        ) < 0

                          ? "text-danger fw-bold"

                          : ""
                      }>
                        {item.qty || 0}
                      </td>

                      <td>
                        {item.defective_stock || 0}
                      </td>

                      <td>
                        {item.scrap_stock || 0}
                      </td>

                      <td>
                        {item.updated_at?.seconds
                          ? new Date(item.updated_at.seconds * 1000).toLocaleString()
                          : item.created_at?.seconds
                          ? new Date(item.created_at.seconds * 1000).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                  )
                )

              ) : (

                <tr>

                  <td
                    colSpan="8"
                    className="text-center"
                  >
                    No stock found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}